import { inject, injectable } from '../../modules/common/di';
import { BaseService } from './baseService';
import { AuthService } from './authService';
import { NotificationsApi } from '../api/admin/notifications/notificationsApi';
import { GetNotificationGroupsApiResponse } from '../api/admin/notifications/getNotificationGroups';
import { CreateNotificationGroupApiResponse, CreateNotificationGroupModel } from '../api/admin/notifications/createNotificationGroup';
import { ApiResponseBase } from '../models/apiResponseBase';
import { UpdateNotificationUsersApiResponse, UpdateNotificationUsersModel } from '../api/admin/notifications/updateNotificationUsersApiResponse';
import { GetNotificationGroupAssignmentsApiResponse } from '../api/admin/notifications/getNotificationGroupAssignments';
import { UpdateNotificationGroupAssignmentsApiResponse, UpdateNotificationGroupAssignmentsModel } from '../api/admin/notifications/updateNotificationGroupAssignments';
import { testOrganizationNotificationsModel } from '../api/admin/notifications/testOrganizationNotifications';

/**
 * Exposes interface to manage organizations. Mostly used by administrator users from Maestro application.
 */
@injectable('NotificationService')
export class NotificationService extends BaseService {
  @inject('NotificationsApi') protected readonly notificationsApi!: NotificationsApi;
  @inject('AuthService') private readonly authService!: AuthService;

  constructor() {
    super();
  }

  /**
   * Returns a list of notification groups for the specified organization.
   * @param {number} organizationId Organization ID.
   * @param {number} [groupId] Optional group ID to filter the results.
   * @returns {Promise<GetNotificationGroupsApiResponse>}
   */
  public getNotificationGroups(organizationId: number, groupId?: number): Promise<GetNotificationGroupsApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.notificationsApi.getNotificationGroups(organizationId, { groupId: groupId }));
  }

  /**
   * Creates or updates a notification group for the specified organization.
   * @param {number} organizationId Organization ID.
   * @param {CreateNotificationGroupModel} group Notification group to create or update.
   * @returns {Promise<CreateNotificationGroupApiResponse>}
   */
  public createOrUpdateNotificationGroup(organizationId: number, group: CreateNotificationGroupModel): Promise<CreateNotificationGroupApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.notificationsApi.createOrUpdateNotificationGroup(organizationId, group));
  }

  /**
   * Deletes a notification group for the specified organization.
   * @param {number} organizationId Organization ID.
   * @param {number} groupId Group ID to delete.
   * @returns {Promise<ApiResponseBase>}
   */
  public deleteNotificationGroup(organizationId: number, groupId: number): Promise<ApiResponseBase> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }
    if (groupId < 1 || isNaN(groupId)) {
      return this.reject(`Group ID is incorrect [${groupId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.notificationsApi.deleteNotificationGroup(organizationId, { groupId: groupId }));
  }

  /**
   * Update notification users in norganization notificaiton groups.
   * @param {number} organizationId Organization to manage notification users.
   * @param {UpdateNotificationUsersModel} users Model of the users to update.
   * @returns {Promise<UpdateNotificationUsersApiResponse>}
   */
  public updateNotificationUsers(organizationId: number, users: UpdateNotificationUsersModel): Promise<UpdateNotificationUsersApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.notificationsApi.updateNotificationUsers(organizationId, users));
  }

  /**
   * Gets notification group assignments for the specified organization.
   * @param {number} organizationId Organization ID.
   * @returns {Promise<GetNotificationGroupAssignmentsApiResponse>}
   */
  public getNotificationGroupAssignments(organizationId: number): Promise<GetNotificationGroupAssignmentsApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.notificationsApi.getNotificationGroupAssignments(organizationId));
  }

  /**
   * Updates notification group assignments for the specified organization.
   * @param {number} organizationId Organization ID.
   * @param {UpdateNotificationGroupAssignmentsModel} assignments Model of the assignments to update.
   * @returns {Promise<UpdateNotificationGroupAssignmentsApiResponse>}
   */
  public updateNotificationGroupAssignments(organizationId: number, assignments: UpdateNotificationGroupAssignmentsModel): Promise<UpdateNotificationGroupAssignmentsApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.notificationsApi.updateNotificationGroupAssignments(organizationId, assignments));
  }

  /**
   * Tests organization notifications for the specified organization.
   * @param {number} organizationId Organization ID.
   * @param {testOrganizationNotificationsModel} model Model of the notification to test.
   * @returns {Promise<ArrayBuffer | string | object>}
   */
  public testOrganizationNotifications(organizationId: number, model: testOrganizationNotificationsModel): Promise<ArrayBuffer | string | object> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.notificationsApi.testOrganizationNotifications(organizationId, model));
  }

}
